import axios, { type AxiosResponse } from 'axios';

import { COREAPI, encodeData, getUserData, ISSESSION } from '../constants/global';
import type { ISupporterElectionSummary, ISupporterProfile } from '../interface/supporter';
import { extractApiErrorMessage } from './api-error';

/**
 * api.core reads for an Independent Supporter's home (`/api/v1/Supporter/*`).
 *
 * <p>Standalone, like the observer client, and with NO blanket 401 → logout: a single failed read
 * must not throw the supporter out. What it does instead is classify. A supporter's access token
 * lives fifteen minutes; api.core's account gate refuses a suspended or closed supporter's token
 * with a bare 401 at once, even while the gate is in observe mode for everyone else. So a 401 is
 * answered with ONE refresh and ONE retry, and only a 401 that survives that — or a refresh the
 * server refuses — is reported as `ended`. The caller decides what ending looks like.</p>
 */
const supporterApi = axios.create({ baseURL: COREAPI, timeout: 15_000 });

const SUPPORTER = '/api/v1/Supporter';

export type SupporterOutcome<T> =
	| { kind: 'ok'; data: T }
	/** The session is over: the token was refused and could not be refreshed. */
	| { kind: 'ended'; message: string | null }
	/** api.core answered and said no (403 not-active / not-followed, 404 not a supporter, 400). */
	| { kind: 'refused'; status: number; message: string | null }
	| { kind: 'unreachable' };

const safeJson = (text: unknown): unknown => {
	if (typeof text !== 'string' || !text) return text;
	try {
		return JSON.parse(text);
	} catch {
		return text;
	}
};

const dataOf = (body: unknown): unknown => {
	const parsed = safeJson(body);
	return parsed && typeof parsed === 'object' && 'data' in parsed ? parsed.data : undefined;
};

const messageOf = (res: AxiosResponse | undefined): string | null => {
	if (!res) return null;
	return extractApiErrorMessage(safeJson(res.data)) || null;
};

type RefreshResult = 'refreshed' | 'refused' | 'unreachable';

let pendingRefresh: Promise<{ result: RefreshResult; message: string | null }> | null = null;

/**
 * Rotate the access token with the refresh token (same endpoint the Redux thunks use). Single
 * flight: the home fires two reads at once, and two rotations of one refresh token race each other.
 */
export const refreshSupporterSession = (): Promise<{ result: RefreshResult; message: string | null }> => {
	if (pendingRefresh) return pendingRefresh;
	pendingRefresh = (async () => {
		const { refreshToken } = getUserData();
		if (!refreshToken) return { result: 'refused' as const, message: null };
		try {
			const res = await axios.post(`${COREAPI}/api/v1/Auth/extend-session`, undefined, {
				headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${refreshToken}` },
				timeout: 15_000,
				validateStatus: () => true,
			});
			const data = dataOf(res.data) as { token?: string; refreshToken?: string } | undefined;
			if (res.status >= 200 && res.status < 300 && data?.token) {
				encodeData(data.token, data.refreshToken);
				return { result: 'refreshed' as const, message: null };
			}
			if (res.status === 401 || res.status === 403) return { result: 'refused' as const, message: messageOf(res) };
			return { result: 'unreachable' as const, message: null };
		} catch {
			return { result: 'unreachable' as const, message: null };
		} finally {
			pendingRefresh = null;
		}
	})();
	return pendingRefresh;
};

const send = (path: string) => {
	const { token, user } = getUserData();
	return supporterApi.get(path, {
		validateStatus: () => true,
		headers: {
			...(token ? { Authorization: `Bearer ${token}` } : {}),
			...(user?.PartyId ? { PartyId: user.PartyId } : {}),
		},
	});
};

const read = async <T>(path: string, accept: (data: unknown) => data is T): Promise<SupporterOutcome<T>> => {
	try {
		if (!ISSESSION()) {
			const refresh = await refreshSupporterSession();
			if (refresh.result === 'refused') return { kind: 'ended', message: refresh.message };
			if (refresh.result === 'unreachable') return { kind: 'unreachable' };
		}

		let res = await send(path);
		if (res.status === 401) {
			const refresh = await refreshSupporterSession();
			if (refresh.result === 'refused') return { kind: 'ended', message: refresh.message ?? messageOf(res) };
			if (refresh.result === 'unreachable') return { kind: 'unreachable' };
			res = await send(path);
			if (res.status === 401) return { kind: 'ended', message: messageOf(res) };
		}

		if (res.status >= 200 && res.status < 300) {
			const data = dataOf(res.data);
			return accept(data) ? { kind: 'ok', data } : { kind: 'unreachable' };
		}
		if (res.status >= 500) return { kind: 'unreachable' };
		return { kind: 'refused', status: res.status, message: messageOf(res) };
	} catch {
		return { kind: 'unreachable' };
	}
};

const isObject = (v: unknown): v is Record<string, unknown> => !!v && typeof v === 'object' && !Array.isArray(v);

const isProfile = (v: unknown): v is ISupporterProfile => isObject(v) && Array.isArray(v.elections);

const isSummary = (v: unknown): v is ISupporterElectionSummary => isObject(v) && isObject(v.pollingUnit);

/** Who the supporter follows and which elections their account is linked to. */
export const getSupporterMe = () => read(`${SUPPORTER}/me`, isProfile);

/** The party's upload coverage in one linked election. 403 for an election the account does not follow. */
export const getSupporterSummary = (electionId: number) => read(`${SUPPORTER}/elections/${electionId}/summary`, isSummary);
